import React from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSectionOpen } from './useSectionOpen';
import styles from './controls.module.css';

/** A titled group of controls that opens and closes from its title; `id` remembers which. */
export const Section = ({ id, icon, title, children }) => {
    const [isOpen, toggle] = useSectionOpen(id);
    const bodyId = `np-section-${id}`;
    return (
        <section className={styles.section}>
            <header className={styles.sectionHeader}>
                <button
                    type="button"
                    className={styles.sectionToggle}
                    aria-expanded={isOpen}
                    aria-controls={bodyId}
                    onClick={toggle}
                >
                    <span className={styles.sectionIcon}>{icon}</span>
                    <h4 className={styles.sectionTitle}>{title}</h4>
                </button>
                {/* Last on the row. A mouse shortcut only: the title is the toggle that keyboards and screen
                    readers use, so this one stays out of the tab order. */}
                <button type="button" className={styles.chevronBtn} tabIndex={-1} aria-hidden="true" onClick={toggle}>
                    <ExpandMoreIcon className={`${styles.chevron} ${isOpen ? '' : styles.chevronClosed}`.trim()} />
                </button>
            </header>
            {isOpen && children && <div id={bodyId} className={styles.sectionBody}>{children}</div>}
        </section>
    );
};

/** A captioned set of controls inside a section, with an optional control (a switch) at the caption's end. */
export const Group = ({ label, action, children }) => (
    <div className={styles.group}>
        <div className={styles.groupHeader}>
            <span className={styles.groupLabel}>{label}</span>
            {action}
        </div>
        {children}
    </div>
);

/** An icon button that stays highlighted while `on` (the aspect lock). */
export const IconToggle = ({ on, title, onClick, children }) => (
    <button
        type="button"
        className={`${styles.iconBtn} ${on ? styles.iconBtnOn : ''}`.trim()}
        aria-pressed={on}
        aria-label={title}
        title={title}
        onClick={onClick}
    >
        {children}
    </button>
);
